import { useMutation, useQueryClient } from '@tanstack/react-query';
import { login as loginService, LoginData } from '@/features/auth/services';
import { ApiResponseError } from '@/shared/types/api';
import useStorage from '@/shared/hooks/use-storage';
import { ROUTE_PATHS } from '@/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { identifyUser, clearUser } from '@/shared/services/sentry';
import { clearAllPlanogramTicketValidationState } from '@/features/planogram-tasks/hooks/use-publish-planogram-ticket-validation-event';

const useAuth = () => {
  const { store } = useStorage();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: async (loginData: LoginData) => {
      const res = await loginService(loginData);
      await store.setAuth(res);
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['auth-profile'] });
      identifyUser({ id: res.user.id, email: res.user.email, username: res.user.name });
    },
  });

  const logout = async (redirect?: boolean) => {
    clearUser();
    await store.clear();
    // Direct dependency of `auth` (generic) on `planogram-tasks` (a specific feature) - acceptable
    // for a single feature's session cleanup, but if another feature needs the same on logout,
    // refactor to a generic "cleanup on logout" registry instead of `auth` importing each one.
    clearAllPlanogramTicketValidationState();

    navigate({
      pathname: ROUTE_PATHS.login,
      search: redirect ? `?redirect=${pathname + search}` : '',
    });
  };

  return {
    login: loginMutation.mutate,
    logout,
    isPending: loginMutation.isPending,
    isSuccess: loginMutation.isSuccess,
    error: loginMutation.error as ApiResponseError | null,
    data: loginMutation.data || null,
    isError: loginMutation.isError,
  };
};

export default useAuth;
